import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Send } from 'lucide-react';
import Logo from '../components/Logo';
import axios from 'axios';

const ProQuoteRequestPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    subject: '',
    message: '',
    quantity: '',
    delivery_address: '',
    delivery_postal_code: '',
    delivery_city: ''
  });

  const [files, setFiles] = useState([]);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('pro_token');
    const userData = localStorage.getItem('pro_user');

    if (!token || !userData) {
      navigate('/pro/login');
      return;
    }

    const parsedUser = JSON.parse(userData);
    setUser(parsedUser);

    // Pre-fill delivery address with user's address
    setFormData(prev => ({
      ...prev,
      delivery_address: parsedUser.address,
      delivery_postal_code: parsedUser.postal_code,
      delivery_city: parsedUser.city
    }));
  }, [navigate]);

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    
    // Validate file sizes (max 10MB each)
    const validFiles = selectedFiles.filter(file => {
      if (file.size > 10 * 1024 * 1024) {
        setError(`Le fichier ${file.name} dépasse 10MB`);
        return false;
      }
      return true;
    });

    setFiles(prev => [...prev, ...validFiles]);
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const formDataToSend = new FormData();
      
      // Add user info
      formDataToSend.append('name', `${user.prenom} ${user.nom}`);
      formDataToSend.append('email', user.email);
      formDataToSend.append('phone', user.phone);
      formDataToSend.append('company', user.company_name);
      formDataToSend.append('siret', user.siret);
      
      // Add form data
      formDataToSend.append('subject', formData.subject);
      formDataToSend.append('message', formData.message);
      formDataToSend.append('quantity', formData.quantity);
      formDataToSend.append('delivery_address', formData.delivery_address);
      formDataToSend.append('delivery_postal_code', formData.delivery_postal_code);
      formDataToSend.append('delivery_city', formData.delivery_city);
      
      // Add files
      files.forEach(file => {
        formDataToSend.append('files', file);
      });

      await axios.post(`${BACKEND_URL}/api/quote-request/`, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setSuccess('Votre demande de devis a été envoyée avec succès ! Nous vous répondrons dans les plus brefs délais.');
      
      // Reset form
      setFormData({
        subject: '',
        message: '',
        quantity: '',
        delivery_address: user.address,
        delivery_postal_code: user.postal_code,
        delivery_city: user.city
      });
      setFiles([]);

      // Redirect after 3 seconds
      setTimeout(() => {
        navigate('/pro/dashboard');
      }, 3000);

    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de l\'envoi de la demande');
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-celadon"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <Logo showTagline={true} clickable={true} />
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Back Button */}
        <button
          onClick={() => navigate('/pro/dashboard')}
          className="flex items-center gap-2 text-celadon hover:text-celadon-light mb-6"
        >
          <ArrowLeft size={20} />
          Retour au Dashboard
        </button>

        {/* Title */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Demande de Devis PRO</h1>
          <p className="text-gray-600">
            Remplissez le formulaire ci-dessous pour recevoir un devis personnalisé avec vos tarifs préférentiels ({user.discount_percentage}% de remise)
          </p>
        </div>

        {/* Messages */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}
        {success && (
          <div className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
            {success}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-8">
          {/* User Info (Read-only) */}
          <div className="mb-6 pb-6 border-b">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Vos informations</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Société</label>
                <input
                  type="text"
                  value={user.company_name}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded bg-gray-50"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">SIRET</label>
                <input
                  type="text"
                  value={user.siret}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded bg-gray-50"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contact</label>
                <input
                  type="text"
                  value={`${user.prenom} ${user.nom}`}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded bg-gray-50"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={user.email}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded bg-gray-50"
                />
              </div>
            </div>
          </div>

          {/* Quote Details */}
          <div className="mb-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Détails de la demande</h3>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Objet du devis *</label>
              <input
                type="text"
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-celadon"
                required
                placeholder="Ex: Toile de store banne sur-mesure"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Description détaillée *</label>
              <textarea
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                rows="6"
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-celadon"
                required
                placeholder="Décrivez votre projet en détail : dimensions, couleurs, options, quantités, délais souhaités..."
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Quantité estimée</label>
              <input
                type="text"
                value={formData.quantity}
                onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-celadon"
                placeholder="Ex: 5 stores, 10 lambrequins..."
              />
            </div>
          </div>

          {/* Delivery Address */}
          <div className="mb-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Adresse de livraison</h3>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Adresse *</label>
              <input
                type="text"
                value={formData.delivery_address}
                onChange={(e) => setFormData({ ...formData, delivery_address: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-celadon"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Code postal *</label>
                <input
                  type="text"
                  value={formData.delivery_postal_code}
                  onChange={(e) => setFormData({ ...formData, delivery_postal_code: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-celadon"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ville *</label>
                <input
                  type="text"
                  value={formData.delivery_city}
                  onChange={(e) => setFormData({ ...formData, delivery_city: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-celadon"
                  required
                />
              </div>
            </div>
          </div>

          {/* File Upload */}
          <div className="mb-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Pièces jointes</h3>
            <p className="text-sm text-gray-600 mb-3">Ajoutez des plans, photos, ou tout document utile (max 10MB par fichier)</p>
            
            <input
              type="file"
              multiple
              onChange={handleFileChange}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-celadon"
              accept="image/*,.pdf"
            />

            {files.length > 0 && (
              <div className="mt-4 space-y-2">
                {files.map((file, index) => (
                  <div key={index} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded">
                    <span className="text-sm text-gray-700 truncate">{file.name}</span>
                    <button
                      type="button"
                      onClick={() => removeFile(index)}
                      className="text-red-500 hover:text-red-700 text-sm"
                    >
                      Supprimer
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 bg-celadon text-white py-3 rounded-lg font-medium hover:bg-celadon-light transition-colors disabled:opacity-50"
          >
            <Send size={20} />
            {loading ? 'Envoi en cours...' : 'Envoyer la demande de devis'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ProQuoteRequestPage;
